import type { notesMapping } from '../indexMappings/notes'
import { buildUrl } from '../utils/build-url'
import { fetchThrowsIfNotOk } from '../utils/fetch-throws-if-not-ok'
import type {
  SearchObjectFieldsTypeFromMappingObjectType,
  SearchResponse,
  SearchResult,
} from './types'

type SearchNotesFields = SearchObjectFieldsTypeFromMappingObjectType<
  typeof notesMapping
>

export type SearchNotesRequiredFields = Required<{
  [K in keyof Pick<
    SearchNotesFields,
    | 'fullPath'
    | 'noteId'
    | 'title'
    | 'noteId'
    | 'channelId'
    | 'channelType'
    | 'iconShape'
    | 'iconColor'
    | 'isArchived'
    | 'type'
    | 'parentNotesTitleList'
    | 'parentNotesIdList'
    | 'channelPurpose'
    | 'updatedAt'
    | 'reviewState'
    | 'archivedAt'
    | 'lastEditedAt'
  >]: SearchNotesFields[K]
}>

// Only used to have type safety and SEARCH_NOTE_REQUIRED_FIELDS matching SearchRequiredFields type definition
const searchNotesRequiredFieldsHelper: {
  [K in keyof SearchNotesRequiredFields]: true
} = {
  fullPath: true,
  noteId: true,
  title: true,
  channelId: true,
  channelType: true,
  iconColor: true,
  iconShape: true,
  isArchived: true,
  type: true,
  parentNotesTitleList: true,
  parentNotesIdList: true,
  channelPurpose: true,
  updatedAt: true,
  reviewState: true,
  archivedAt: true,
  lastEditedAt: true,
}

export const SEARCH_NOTES_REQUIRED_FIELDS = Object.freeze(
  Object.keys(searchNotesRequiredFieldsHelper)
)

export type SearchNotesResult = SearchResult<SearchNotesRequiredFields>

export type SearchNotesResponse = SearchResponse<SearchNotesResult> & {
  isQuestion?: boolean
}

export type SimilarNoteRequiredFields = Required<{
  [K in keyof Pick<
    SearchNotesFields,
    | 'title'
    | 'channelId'
    | 'noteId'
    | 'iconShape'
    | 'iconColor'
    | 'channelType'
    | 'fullPath'
  >]: SearchNotesFields[K]
}>

const similarNotesRequiredFieldsHelper: {
  [K in keyof SimilarNoteRequiredFields]: true
} = {
  noteId: true,
  title: true,
  channelId: true,
  iconColor: true,
  iconShape: true,
  channelType: true,
  fullPath: true,
}

export const SIMILAR_NOTES_REQUIRED_FIELDS = Object.freeze(
  Object.keys(similarNotesRequiredFieldsHelper)
)

export type SimilarNotesResult = SearchResult<SimilarNoteRequiredFields>
export type SimilarNotesResponse = SearchResponse<SimilarNotesResult>

export enum FragmentTypes {}

export type SearchNotesOptionalFilters = {
  externalTypes?: string[]
  excludeExternalTypes?: string[]
  excludeParentNoteIdList?: string[]
  parentNoteIdList?: string[]
  excludeChannelIdList?: string[]
  channelIdList?: string[]
  reviewState?: string[]
  excludeReviewStates?: string[]
  excludeNoteTypes?: string[]
  noteTypes?: string[]
  excludeFragmentTypes?: FragmentTypes[]
  fragmentTypes?: FragmentTypes[]
  lastEditedAfter?: string // Should be Date ISO string
  lastEditedBefore?: string // Should be Date ISO string
  lastUpdatedAfter?: string // Should be Date ISO string
  lastUpdatedBefore?: string // Should be Date ISO string
  updatedCloseTo?: string // Should be Date ISO string
  decayScale?: number // Used with updatedCloseTo to decay documents relative to this date
  parentNoteTitleList?: string[]
  onlyArchived?: boolean
  onlyPubliclyShared?: boolean
  excludeNoteIdList?: string[]
  depth?: number // Used to filter notes by depth in the tree
  /** Legacy filters */
  updatedBefore?: string // Should be Date ISO string
  updatedAfter?: string // Should be Date ISO string
  channelId?: string
  includeArchived?: boolean
  onlyPrivateAndPersonalChannels?: boolean
  onlyDiscussions?: boolean
  authorIds?: string[]
  channelPurposes?: string[]
  noteType?: string
}

export enum SearchNotesClient {
  App = 'app',
  PublicApi = 'public_api',
  Mcp = 'mcp',
}

export type SearchNotesQuery = {
  query: string
  optionalFilters?: SearchNotesOptionalFilters
  hitsPerPage: number
  page: number
  highlightPreTag: string
  highlightPostTag: string
  explain?: boolean
  profile?: boolean
}

export type SimilarNotesQuery = {
  hitsPerPage: number
  page: number
  noteId: string
}

export async function handleError(response: Response) {
  if (!response.ok) {
    let error: Error
    try {
      const { message } = await response.clone().json()
      error = Error(message ?? response.statusText)
    } catch {
      error = Error(response.statusText)
    }

    throw error
  }
  return response
}

export async function fetchNotes({
  baseUrl,
  token,
  query,
  highlightPostTag,
  highlightPreTag,
  hitsPerPage,
  optionalFilters,
  page,
}: {
  baseUrl: string
  token: string
} & SearchNotesQuery): Promise<SearchNotesResponse> {
  const response = await fetchThrowsIfNotOk(buildUrl(baseUrl, '/notes'), {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    method: 'post',
    body: JSON.stringify({
      query,
      optionalFilters,
      highlightPostTag,
      highlightPreTag,
      hitsPerPage,
      page,
    }),
  })

  await handleError(response)
  return await response.json()
}

export type SearchNotesAutocompleteRequiredFields = Required<{
  [K in keyof Pick<
    SearchNotesFields,
    | 'channelId'
    | 'channelType'
    | 'channelPurpose'
    | 'fullPath'
    | 'hasChildren'
    | 'iconColor'
    | 'iconShape'
    | 'iconVariant'
    | 'isArchived'
    | 'isChannelDoc'
    | 'isCollectionRoot'
    | 'isPubliclyShared'
    | 'isTemplate'
    | 'reviewState'
    | 'noteId'
    | 'parentNotesIdList'
    | 'parentNotesTitleList'
    | 'title'
    | 'type'
    | 'coverImageUrl'
  >]: SearchNotesFields[K]
}>

// Only used to have type safety and SEARCH_NOTE_AUTOCOMPLETE_REQUIRED_FIELDS matching SearchRequiredFields type definition
const searchNotesAutocompleteRequiredFieldsHelper: {
  [K in keyof SearchNotesAutocompleteRequiredFields]: true
} = {
  channelId: true,
  channelType: true,
  fullPath: true,
  hasChildren: true,
  iconColor: true,
  iconShape: true,
  iconVariant: true,
  isArchived: true,
  isChannelDoc: true,
  isCollectionRoot: true,
  noteId: true,
  parentNotesIdList: true,
  parentNotesTitleList: true,
  title: true,
  type: true,
  channelPurpose: true,
  isPubliclyShared: true,
  isTemplate: true,
  reviewState: true,
  coverImageUrl: true,
}

export const SEARCH_NOTE_AUTOCOMPLETE_REQUIRED_FIELDS = Object.freeze(
  Object.keys(searchNotesAutocompleteRequiredFieldsHelper)
)

export type SearchNotesAutocompleteResult =
  SearchResult<SearchNotesAutocompleteRequiredFields>

export type SearchNotesAutocompleteResponse =
  SearchResponse<SearchNotesAutocompleteResult>

export enum NoteAutocompleteSearchableField {
  Title = 'title',
}

export enum NoteAutocompleteType {
  RichText = 'rich_text',
  Discussion = 'discussion',
  Collection = 'collection',
}

export type SearchNotesAutocompleteOptionalFilters = {
  hasChildren?: boolean
  isArchived?: boolean
  restrictSearchableFieldList?: NoteAutocompleteSearchableField[]
  isTemplate?: boolean
  isChannelDoc?: boolean
  isPubliclyShared?: boolean
  noteTypes?: NoteAutocompleteType[]
  channelPurposes?: string[]
  externalTypes?: string[]
  excludeExternalTypes?: string[]
}

export type SearchNotesAutocompleteQuery = {
  query: string
  optionalFilters?: SearchNotesAutocompleteOptionalFilters
  hitsPerPage: number
  explain?: boolean
  profile?: boolean
  // We don't support pagination anymore for autocomplete
  // as we are using 2 different indexes for the search
  // and we were not using it anyway (page is always 0)
  page?: 0
}

export type SearchNotesAutocompleteHit = {
  id: string
  channelId: string
  channelType: string
  channelPurpose: string
  fullPath: string
  hasChildren: boolean
  iconColor: string | null
  iconShape: string | null
  iconVariant: string | null
  isArchived: boolean
  isChannelDoc: boolean
  isCollectionRoot: boolean
  parentNotesIdList: string[]
  parentNotesTitleList: string[]
  title: string
  type: NoteAutocompleteType
  isPubliclyShared: boolean
  isTemplate: boolean
  reviewState: string
  coverImageUrl?: string
}

export type SimilarNotesHit = {
  id: string
  channelId: string
  channelType: string
  fullPath: string
  iconColor: string
  iconShape: string
  title: string
  score: number
}

function notEmpty<T>(value?: T | null): value is NonNullable<T> {
  return value != null
}

function noteAutocompleteTypeFromString(type?: string): NoteAutocompleteType {
  switch (type) {
    case 'rich_text':
      return NoteAutocompleteType.RichText
    case 'discussion':
      return NoteAutocompleteType.Discussion
    case 'collection':
      return NoteAutocompleteType.Collection
    default:
      throw new Error(`Unknown note type: ${type}`)
  }
}

export async function fetchNotesAutocomplete({
  baseUrl,
  token,
  query,
  hitsPerPage,
  optionalFilters,
  page,
}: {
  baseUrl: string
  token: string
} & SearchNotesAutocompleteQuery): Promise<
  Pick<SearchNotesAutocompleteResponse, 'nbPages' | 'page'> & {
    hits: SearchNotesAutocompleteHit[]
  }
> {
  const response = await fetchThrowsIfNotOk(
    buildUrl(baseUrl, '/notes-autocomplete'),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      method: 'post',
      body: JSON.stringify({
        optionalFilters,
        query,
        hitsPerPage,
        page,
      }),
    }
  )

  await handleError(response)

  const result: SearchNotesAutocompleteResponse = await response.json()

  const hits: SearchNotesAutocompleteHit[] = result.hits.map((hit) => ({
    id: hit.fields?.noteId?.[0] ?? '',
    channelId: hit.fields?.channelId?.[0] ?? '',
    channelType: hit.fields?.channelType?.[0] ?? '',
    fullPath: hit.fields?.fullPath?.[0] ?? '',
    hasChildren: hit.fields?.hasChildren?.[0] ?? false,
    iconColor: hit.fields?.iconColor?.[0] ?? '',
    iconShape: hit.fields?.iconShape?.[0] ?? '',
    iconVariant: hit.fields?.iconVariant?.[0] ?? '',
    isArchived: hit.fields?.isArchived?.[0] ?? false,
    isChannelDoc: hit.fields?.isChannelDoc?.[0] ?? false,
    isCollectionRoot: hit.fields?.isCollectionRoot?.[0] ?? false,
    parentNotesIdList: hit.fields?.parentNotesIdList?.filter(notEmpty) ?? [],
    parentNotesTitleList:
      hit.fields?.parentNotesTitleList?.filter(notEmpty) ?? [],
    title: hit.fields?.title?.[0] ?? '',
    type: noteAutocompleteTypeFromString(hit.fields?.type?.[0]),
    channelPurpose: hit.fields?.channelPurpose?.[0] ?? '',
    isPubliclyShared: hit.fields?.isPubliclyShared?.[0] ?? false,
    isTemplate: hit.fields?.isTemplate?.[0] ?? false,
    reviewState: hit.fields?.reviewState?.[0] ?? '',
    coverImageUrl: hit.fields?.coverImageUrl?.[0] ?? '',
  }))

  return {
    nbPages: result.nbPages,
    page: result.page,
    hits,
  }
}

export async function fetchSimilarNotes({
  baseUrl,
  token,
  hitsPerPage,
  page,
  noteId,
}: {
  baseUrl: string
  token: string
} & SimilarNotesQuery): Promise<
  Pick<SimilarNotesResponse, 'nbPages' | 'page'> & {
    hits: SimilarNotesHit[]
  }
> {
  const response = await fetchThrowsIfNotOk(
    buildUrl(baseUrl, '/similar-notes'),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      method: 'post',
      body: JSON.stringify({
        noteId,
        hitsPerPage,
        page,
      }),
    }
  )

  const result: SimilarNotesResponse = await response.json()

  const hits: SimilarNotesHit[] = result.hits
    .filter((hit) => hit._score && hit._score > 50)
    .map((hit) => ({
      id: hit.fields?.noteId?.[0] ?? '',
      channelId: hit.fields?.channelId?.[0] ?? '',
      channelType: hit.fields?.channelType?.[0] ?? '',
      fullPath: hit.fields?.fullPath?.[0] ?? '',
      iconColor: hit.fields?.iconColor?.[0] ?? '',
      iconShape: hit.fields?.iconShape?.[0] ?? '',
      title: hit.fields?.title?.[0] ?? '',
      score: hit._score ?? 0,
    }))
  return {
    nbPages: result.nbPages,
    page: result.page,
    hits,
  }
}
